// FILE: useOlderThreadHistory.ts
// Purpose: Fetch older pages of a thread's transcript on demand and splice them into the store.
// Layer: Chat transcript data access
// Depends on: the orchestration WS api and the thread history window kept in the app store.

import type { ThreadId } from "@taskin-code/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { readNativeApi } from "~/nativeApi";
import { useStore } from "../../store";
import {
  createThreadHasMoreHistorySelector,
  createThreadOldestHistoryCursorSelector,
} from "../../storeSelectors";

export interface OlderThreadHistoryControls {
  /** There is more transcript above what the store holds. */
  hasMoreHistory: boolean;
  /** A page is in flight; the transcript shows its loading affordance. */
  isLoadingOlderHistory: boolean;
  /** Request the next page back. Safe to call repeatedly — extra calls no-op. */
  loadOlderHistory: () => void;
}

/**
 * Paging back through a thread's history.
 *
 * The transcript only ever holds the newest slice of a long thread, so scrolling
 * to the top has to go back to the server. This owns the one piece of state the
 * timeline cannot: whether a request is already out.
 *
 * The in-flight guard is a ref rather than the rendered boolean because the
 * trigger is a scroll handler — several events fire inside a single frame, long
 * before React commits `isLoadingOlderHistory`, and a state-only guard would let
 * every one of them through.
 *
 * Failures are swallowed on purpose: a page that does not arrive leaves the
 * transcript exactly as it was, and the user's next scroll retries. Surfacing an
 * error for history that is merely *older* would be louder than the problem.
 */
export function useOlderThreadHistory(threadId: ThreadId | null): OlderThreadHistoryControls {
  const hasMoreHistory = useStore(
    useMemo(() => createThreadHasMoreHistorySelector(threadId), [threadId]),
  );
  const oldestCursor = useStore(
    useMemo(() => createThreadOldestHistoryCursorSelector(threadId), [threadId]),
  );
  const prependThreadHistory = useStore((store) => store.prependThreadHistory);
  const [isLoadingOlderHistory, setIsLoadingOlderHistory] = useState(false);
  const inFlightRef = useRef(false);

  // Switching threads abandons whatever was in flight for the previous one: its
  // page would be spliced into a transcript nobody is looking at, and the guard
  // would stay stuck on for the thread now on screen.
  useEffect(() => {
    inFlightRef.current = false;
    setIsLoadingOlderHistory(false);
  }, [threadId]);

  const loadOlderHistory = useCallback(() => {
    if (threadId === null || oldestCursor === undefined || !hasMoreHistory) return;
    if (inFlightRef.current) return;
    const api = readNativeApi();
    if (!api) return;

    inFlightRef.current = true;
    setIsLoadingOlderHistory(true);
    void api.orchestration
      .loadOlderThreadHistory({ threadId, beforeCursor: oldestCursor })
      .then((page) => {
        prependThreadHistory(threadId, page);
      })
      .catch(() => undefined)
      .finally(() => {
        inFlightRef.current = false;
        setIsLoadingOlderHistory(false);
      });
  }, [hasMoreHistory, oldestCursor, prependThreadHistory, threadId]);

  return { hasMoreHistory, isLoadingOlderHistory, loadOlderHistory };
}
